import { EmissionFactor } from '@/apis/report/cfv/emissions.dto';
import { ISICDivision, ISICSection } from '@/app/factory-database/src/enums/isic';
import { ProgressBarStatus, ProgressItem } from '@/components/common/Feedback/progress-bar/typings';
import { NextTFunction } from '@/i18n/client';
import { ScenarioStatus } from '@/modules/ActivityData/domain/ActivityData';

import { RMTRouteType } from '../../../factory-database/src/enums/rmt';
import { CreateEmissionType } from '../../[projectId]/emission-sources/create/formValidation';
import { ActivityUnit, CatOfActivityRelatedToFuelEnergy } from '../enums/ActivityDataTypeEnum';
import {
  EmissionFactorByFactorErrorType,
  EmissionSourceCategory,
  EmissionSourceFactorSource,
  EmissionSourceFactorType,
  EmissionSourceFactorUnitType,
  EmissionSourceGreenHouseGases,
  EmissionSourceMeasurementMethod,
  EmissionSourceSubCategory,
} from '../enums/EmissionSourceEnum';

class CreateEmissionConstants {
  public static readonly PROGRESS_BAR_ITEMS: ProgressItem[] = [
    {
      label: 'basicInformation',
      status: ProgressBarStatus.INCOMPLETE,
    },
    {
      label: 'emissionFactors',
      status: ProgressBarStatus.INCOMPLETE,
    },
    {
      label: 'assign',
      status: ProgressBarStatus.INCOMPLETE,
    },
  ];

  public static readonly CATEGORY_ONE_DEFAULT_VALUES: CreateEmissionType = {
    id: '',
    category: EmissionSourceCategory.CAT_1,
    name: '',
    subCategory: EmissionSourceSubCategory.CAT_1_1,
    assignedUsers: [],
    documents: [],
    metadata: {
      activityDocumentDescription: '',
      rawMaterial1: null,
      rawMaterial2: null,
      rawMaterial4_1b: null,
      rawMaterial4_1bOtherName: '',
      reportRawMaterial1: null,
      reportRawMaterial2: null,
      industryType: null,
      industryTypeOtherName: '',
      industryProcess: null,
      industryProcessOtherName: '',
      rawMaterial2OtherName: '',
      purchasedEnergyType: null,

      fugitiveCategory: null,
      fugitiveCategoryOtherName: '',
      fugitiveType: null,
      fugitiveTypeOtherName: '',
      equipmentType: null,
      equipmentTypeOtherName: '',

      refrigerantComposition: [],
      sewagePool: {
        bodFactor: null,
        averageWastewaterConcentration: null,
        perCapitaHourlyWastewaterVolume: null,
        treatmentEfficiency: null,
      },
      customWastewater: {
        treatmentMethod: null,
        dischargeLocation: null,
        dischargePathwayOrSystemType: null,
        wastewaterTreatmentRate: null,
        maxMethaneProduction: null,
        methaneCorrectionFactor: null,
        organicMatterProductionPPPD: null,
        ch4IndustrialWastewaterDischargeFactor: null,
        ch4EmissionFactor: null,
        proteinSupplyPPPD: null,
        nitrogenToProteinRatio: null,
        nonProteinSaturatedWastewaterFactor: null,
        n2oIndustrialWastewaterDischargeFactor: null,
        n2oEmissionFactor: null,
      },
      unitConversionFactor: null,
      ghgsSetting: null,

      emissionType: null,
      emissionTypeOther: undefined,
      emissionType1: null,
      emissionTypeOther1: undefined,
      emissionType2: null,
      emissionTypeOther2: undefined,
      rawMaterialNumber: null,
      equipmentNumber: undefined,
      processNumber: undefined,
      wasteManagementCode: '',
      wasteSiteCode: '',

      process: '',
      equipment: '',
      greenHouseEmissionType: [],
      isStationarySourcesLicense: false,
      isCoExistingDevice: false,
      isBioEnergySource: false,
      measurementMethod: EmissionSourceMeasurementMethod.EMISSION_FACTORS_APPROACH,
      isForceCustomized: false,

      vendorCode: '',
      vendorPartNo: '',
      shipmentLocationCode: '',
      source: '',
      rawMaterialAcquisitionId: '',
      netWeight: null,
      grossWeight: null,
      grossWeightUnit: null as unknown as ActivityUnit,
      materialUnitType: null as unknown as EmissionSourceFactorUnitType,
      materialUnit: null as unknown as ActivityUnit,
      activityUnitType: null as unknown as EmissionSourceFactorUnitType,
      activityUnit: null as unknown as ActivityUnit,
      transportationRoute: RMTRouteType.DEFAULT,
      primaryCalculatedData: null,
      primaryMaterialUnitType: null as unknown as EmissionSourceFactorUnitType,
      primaryMaterialUnit: null as unknown as ActivityUnit,
      primaryActivityUnitType: null as unknown as EmissionSourceFactorUnitType,
      primaryActivityUnit: null as unknown as ActivityUnit,
      ghgMaterialUnit: null as unknown as ActivityUnit,

      relatedFuelEnergyCategory: null as unknown as CatOfActivityRelatedToFuelEnergy,

      transportationId: null,
      distributionId: null,
      customerCode: null,
      customerName: null,
      receivingLocationId: null,
      receivingLocationCode: null,
      receivingLocationName: null,
      version: null,
      productMaterialActivatedAt: null,
      productMaterialNetWeight: null,
      productMaterialUnit: null as unknown as ActivityUnit,
      productSeriesId: null,
      country: '',
      area: '',
      areaOther: null,
      scenarioCountryAreaId: null,
      scenarioStatus: null as unknown as ScenarioStatus,
      employeeIdentifier: '',
      employeeName: '',
      employeeDivisionDepartment: '',
      commutingRouteName: '',
      departure: null,
      destination: null,
    },
    factors: [
      {
        type: EmissionSourceFactorType.RECOMMEND,
        name: '',
        factorSourceKey: '',
        factorErrorType: null,
        factorSourceType: EmissionSourceFactorSource.DEFAULT,
        greenHouseEmissionType: [],
        gasData: [],
      },
    ],
  };

  private static readonly GAS_DATA = (t: NextTFunction) =>
    [
      EmissionSourceGreenHouseGases.CO2,
      EmissionSourceGreenHouseGases.CH4,
      EmissionSourceGreenHouseGases.HFCS,
      EmissionSourceGreenHouseGases.N2O,
      EmissionSourceGreenHouseGases.NF3,
      EmissionSourceGreenHouseGases.PFCS,
      EmissionSourceGreenHouseGases.SF6,
    ].map((gas) => {
      return {
        factor: 0,
        gasKey: gas,
        gasName: t(`ghg.${gas}`),
        factorUnitType: EmissionSourceFactorUnitType.WEIGHT,
        factorUnit: ActivityUnit.KG,
        ghgUnit: ActivityUnit.KG,
        isRefrigerant: false,
        isPFCS: false,
        isCO2Equivalent: false,
      };
    });

  public static readonly EMISSION_FACTOR = (t: NextTFunction): EmissionFactor => ({
    type: EmissionSourceFactorType.DEFAULT,
    name: '',
    factorSourceKey: '',
    factorErrorType: EmissionFactorByFactorErrorType.FACTOR_ERROR_TYPE_6,
    factorSourceType: EmissionSourceFactorSource.DEFAULT,
    factorSourceName: '',
    gasData: this.GAS_DATA(t),
    greenHouseEmissionType: [],
  });

  public static readonly HAS_RECOMMENDED_EMISSION_FACTORS: EmissionSourceSubCategory[] = [
    EmissionSourceSubCategory.CAT_1_1,
    EmissionSourceSubCategory.CAT_1_2,
  ];

  public static readonly HAS_RECOMMENDED_SUB_CATEGORY_MAPPING_FIELDS: Partial<
    Record<EmissionSourceSubCategory, string[]>
  > = {
    [EmissionSourceSubCategory.CAT_1_1]: ['metadata.rawMaterial2'],
    [EmissionSourceSubCategory.CAT_1_2]: ['metadata.rawMaterial2'],
  };

  public static readonly ONLY_RAW_MATERIAL_SUB_CATEGORIES: EmissionSourceSubCategory[] = [
    EmissionSourceSubCategory.CAT_1_1,
    EmissionSourceSubCategory.CAT_1_2,
  ];

  public static readonly MARKET_LOCATION_SUB_CATEGORIES: EmissionSourceSubCategory[] = [
    EmissionSourceSubCategory.CAT_2_1,
    EmissionSourceSubCategory.CAT_2_2,
  ];

  public static readonly CATEGORY_MAPPING_SUB_CATEGORIES: Partial<
    Record<EmissionSourceCategory, EmissionSourceSubCategory[]>
  > = {
    [EmissionSourceCategory.CAT_1]: [
      EmissionSourceSubCategory.CAT_1_1,
      EmissionSourceSubCategory.CAT_1_2,
      EmissionSourceSubCategory.CAT_1_3,
      EmissionSourceSubCategory.CAT_1_4,
    ],
    [EmissionSourceCategory.CAT_2]: [EmissionSourceSubCategory.CAT_2_1, EmissionSourceSubCategory.CAT_2_2],
    [EmissionSourceCategory.CAT_3]: [
      EmissionSourceSubCategory.CAT_3_1,
      EmissionSourceSubCategory.CAT_3_2,
      EmissionSourceSubCategory.CAT_3_3,
      EmissionSourceSubCategory.CAT_3_4,
      EmissionSourceSubCategory.CAT_3_5,
    ],
    [EmissionSourceCategory.CAT_4]: [
      EmissionSourceSubCategory.CAT_4_1A,
      EmissionSourceSubCategory.CAT_4_1B,
      EmissionSourceSubCategory.CAT_4_2,
      EmissionSourceSubCategory.CAT_4_3,
      EmissionSourceSubCategory.CAT_4_4,
      EmissionSourceSubCategory.CAT_4_5,
    ],
    [EmissionSourceCategory.CAT_5]: [
      EmissionSourceSubCategory.CAT_5_1,
      EmissionSourceSubCategory.CAT_5_2,
      EmissionSourceSubCategory.CAT_5_3,
      EmissionSourceSubCategory.CAT_5_4,
      EmissionSourceSubCategory.CAT_5_5,
      EmissionSourceSubCategory.CAT_5_6,
    ],
    [EmissionSourceCategory.CAT_6]: [EmissionSourceSubCategory.CAT_6_1],
  };

  public static readonly ONLY_SHOW_CO2E_GHG_SUB_CATEGORIES: EmissionSourceSubCategory[] = [
    EmissionSourceSubCategory.CAT_1_3,
    EmissionSourceSubCategory.CAT_2_1,
    EmissionSourceSubCategory.CAT_3_1,
    EmissionSourceSubCategory.CAT_3_2,
    EmissionSourceSubCategory.CAT_4_1A,
    EmissionSourceSubCategory.CAT_4_1B,
    EmissionSourceSubCategory.CAT_4_3,
  ];

  public static readonly DEFAULT_ISIC_OPTIONS: Partial<
    Record<EmissionSourceSubCategory, { sections: ISICSection[]; divisions?: ISICDivision[] }>
  > = {
    [EmissionSourceSubCategory.CAT_1_1]: {
      sections: [ISICSection.Z_SECTION],
      divisions: [ISICDivision.Z_100],
    },
    [EmissionSourceSubCategory.CAT_1_2]: {
      sections: [ISICSection.Z_SECTION],
      divisions: [ISICDivision.Z_100],
    },
    [EmissionSourceSubCategory.CAT_1_3]: {
      sections: [ISICSection.Z_SECTION],
      divisions: [ISICDivision.Z_101],
    },
    [EmissionSourceSubCategory.CAT_1_4]: {
      sections: [ISICSection.Z_SECTION],
      divisions: [ISICDivision.Z_102],
    },
    [EmissionSourceSubCategory.CAT_2_1]: {
      sections: [ISICSection.Z_SECTION],
      divisions: [ISICDivision.Z_103],
    },
    [EmissionSourceSubCategory.CAT_2_2]: {
      sections: [ISICSection.Z_SECTION],
      divisions: [ISICDivision.Z_103],
    },
    [EmissionSourceSubCategory.CAT_3_3]: {
      sections: [ISICSection.H_SECTION],
    },
    [EmissionSourceSubCategory.CAT_4_1B]: {
      sections: [ISICSection.C_SECTION, ISICSection.D_SECTION, ISICSection.E_SECTION, ISICSection.G_SECTION],
    },
    [EmissionSourceSubCategory.CAT_4_3]: {
      sections: [ISICSection.E_SECTION],
    },
    [EmissionSourceSubCategory.CAT_4_5]: {
      sections: [
        ISICSection.F_SECTION,
        ISICSection.G_SECTION,
        ISICSection.H_SECTION,
        ISICSection.I_SECTION,
        ISICSection.J_SECTION,
        ISICSection.K_SECTION,
        ISICSection.M_SECTION,
        ISICSection.N_SECTION,
        ISICSection.O_SECTION,
        ISICSection.P_SECTION,
        ISICSection.Q_SECTION,
        ISICSection.R_SECTION,
        ISICSection.S_SECTION,
        ISICSection.T_SECTION,
        ISICSection.U_SECTION,
      ],
      divisions: [
        ...Object.values(ISICDivision).filter((division) => {
          const prefix = division.split('_')?.[0];
          const allowedPrefixes = ['F', 'G', 'H', 'I', 'J', 'K', 'M', 'N', 'O', 'P', 'Q', 'R', 'S', 'T', 'U'];
          return prefix === 'H' ? division === ISICDivision.H_53 : allowedPrefixes.includes(prefix);
        }),
      ],
    },
  };

  public static readonly TD_LOSS_DEFAULT_ISIC_OPTIONS = {
    sections: [ISICSection.Z_SECTION],
    divisions: [ISICDivision.Z_104],
  };
}

export default CreateEmissionConstants;
